/**
 * McpServiceModule — wraps vibe-designer's /api/chat-proxy/v1/* MCP proxy.
 *
 * Three McpService instances (one per backend) are exposed via
 * `BriefingRoomServices.mcp.{mind|sight|cortex}`. Each routes through
 * the same proxy but with a different `backend` discriminator path
 * segment that the vibe-designer chat_proxy router unpacks.
 *
 * M3a scope: scaffold the query() shape. The actual MCP tool dispatch
 * lands at S5 when the Engine track ships rule-extraction queries.
 * For now: query() POSTs the tool + args; isAvailable() probes
 * /api/knowledge/overview for a 200.
 */
import type { McpService } from '../types';
import { getAuthHeaders } from './authHeaders';

const PROXY_BASE = '/api/chat-proxy/v1';
const HEALTH_PATH = '/api/knowledge/overview';

export type McpBackend = 'mind' | 'sight' | 'cortex';

export class McpServiceModule implements McpService {
  constructor(
    private readonly backend: McpBackend,
    private readonly proxyBase: string = PROXY_BASE,
    private readonly healthPath: string = HEALTH_PATH,
    // See PersistenceServiceModule for the "Illegal invocation" rationale.
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  isAvailable(): boolean {
    return true;
  }

  async query<T = unknown>(tool: string, args: Record<string, unknown>): Promise<T> {
    const url = `${this.proxyBase}/${this.backend}/${encodeURIComponent(tool)}`;
    const resp = await this.fetchFn(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(args),
    });
    if (!resp.ok) {
      throw new Error(
        `McpServiceModule(${this.backend}): ${tool} returned HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as T;
  }

  async healthCheck(): Promise<boolean> {
    try {
      const resp = await this.fetchFn(this.healthPath, {
        headers: getAuthHeaders(),
      });
      return resp.ok;
    } catch {
      return false;
    }
  }
}
